<template>
  <div>
    <reply-by-template v-if="isReplyDisabled" />
    <reply-box-v2
      v-else
      :initial-message.sync="replyMessage"
      :popout-reply-box="isPopoutReplyBox"
      @popout="showPopoutReplyBox"
    />
    <woot-modal
      :show.sync="isPopoutReplyBox"
      :on-close="closePopoutReplyBox"
      :size="'!w-[800px] h-[500px] rounded-lg'"
      :show-close-button="isReplyDisabled"
      class="popout-editor"
    >
      <reply-by-template v-if="isReplyDisabled" />
      <reply-box-v2
        v-else
        :initial-message.sync="replyMessage"
        :popout-reply-box="isPopoutReplyBox"
        @popout="showPopoutReplyBox"
      />
    </woot-modal>
    <online-wa-templates-modal
      v-if="isOnlineWaInboxV2"
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />
    <whatsapp-templates
      v-else
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />
  </div>
</template>
<script>
import ReplyBoxV2 from './ReplyBoxV2.vue';
import ReplyByTemplate from './ReplyByTemplate.vue';
import OnlineWaTemplatesModal from 'dashboard/components/widgets/conversation/watemplates/OnlineWaModal.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';

// stores and apis
import { computed } from 'vue';
import { mapGetters } from 'vuex';

import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import inboxMixin from 'shared/mixins/inboxMixin';
import alertMixin from 'shared/mixins/alertMixin';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';

import DocalAPI from 'dashboard/api/docal';
import FormsAPI from 'dashboard/api/integrations/forms';

import { isEscape } from 'shared/helpers/KeyboardHelpers';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';

const formLinkSuffix = formLink => {
  try {
    const { pathname, search } = new URL(formLink);
    return `${pathname.replace(/^\//, '')}${search}`;
  } catch (error) {
    return formLink;
  }
};

export default {
  components: {
    ReplyBoxV2,
    ReplyByTemplate,
    OnlineWaTemplatesModal,
    WhatsappTemplates,
  },
  mixins: [inboxMixin, hasPermissionMixin, alertMixin, fileUploadMixin],
  provide() {
    return {
      replyType: computed(() => this.replyType),
      isPrivateMode: computed(() => this.isPrivateMode),
      showWhatsAppTemplatesModal: computed(() => this.showWhatsAppTemplatesModal),
      setReplyMode: this.setReplyMode,
      openWhatsappTemplateModal: this.openWhatsappTemplateModal,
      hideWhatsappTemplatesModal: this.hideWhatsappTemplatesModal,
    };
  },
  data() {
    return {
      showWhatsAppTemplatesModal: false,
      replyMessage: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      isPopoutReplyBox: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    isReplyDisabled() {
      return (
        this.isOnlineWaInboxV2 &&
        !this.currentChat?.can_reply &&
        this.replyType === REPLY_EDITOR_MODES.REPLY
      );
    },
    conversationIdByRoute() {
      return this.currentChat.id;
    },
    isPrivateMode() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
  },
  methods: {
    showPopoutReplyBox() {
      this.isPopoutReplyBox = !this.isPopoutReplyBox;
    },
    closePopoutReplyBox() {
      this.isPopoutReplyBox = false;
    },
    handleKeyEvents(e) {
      if (isEscape(e)) {
        this.closePopoutReplyBox();
      }
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? this.$track(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : this.$track(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel) {
        const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
        LocalStorage.updateJsonStore(replyStorageKey, 'mode', mode);
        this.replyType = mode;
      }
      // todo
      // if (this.showRichContentEditor) {
      //   if (this.isRecordingAudio) {
      //     this.toggleAudioRecorder();
      //   }
      //   return;
      // }
      // this.$nextTick(() => this.$refs.messageInput?.focus());
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        bus.$emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      if (messagePayload.doc) {
        try {
          await this.attachSigningLinkToPayload(messagePayload);
        } catch (error) {
          // console.error(error);
          return;
        }
      }
      if (messagePayload.form) {
        try {
          await this.attachFormLinkToPayload(messagePayload);
        } catch (error) {
          this.showAlert(this.$t('FORMS_MODAL.LINK_ERROR'));
          return;
        }
      }
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async attachSigningLinkToPayload(messagePayload) {
      const conversationId = this.currentChat.id;
      const documentId = messagePayload.doc.document_id;
      const response = await DocalAPI.getSigningLink(
        this.accountId,
        documentId,
        conversationId
      );
      const signingLink = response.data?.signing_url;

      const hash = signingLink?.split('/')?.pop();
      if (!messagePayload.contentAttributes) {
        messagePayload.contentAttributes = {};
      }
      if (!messagePayload.contentAttributes.template_info) {
        messagePayload.contentAttributes.template_info = {};
      }
      messagePayload.contentAttributes.template_info.websiteVariable = hash;
      delete messagePayload.doc;
    },
    async attachFormLinkToPayload(messagePayload) {
      const response = await FormsAPI.getFormLink(
        messagePayload.form.publicId,
        this.currentChat.id
      );
      const formLink = response.data?.form_link;
      if (!formLink) throw new Error('Empty form link');

      if (!messagePayload.contentAttributes) {
        messagePayload.contentAttributes = {};
      }
      if (!messagePayload.contentAttributes.template_info) {
        messagePayload.contentAttributes.template_info = {};
      }
      messagePayload.contentAttributes.template_info.websiteVariable =
        formLinkSuffix(formLink);
      delete messagePayload.form;
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .modal-container {
    @apply rounded-lg;

    .reply-box-v2,
    .reply-by-template {
      height: 100%;
    }

    .reply-box-v2__textarea {
      flex: 1;
      display: flex;
      flex-direction: column;

      .editor-container {
        flex: 1;
      }
    }

    .resizable-text-area-wrapper {
      flex: 1;

      textarea {
        flex: 1;
      }
    }
  }
}
</style>
